import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { PageLoader } from "@/components/ui/Spinner";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { VisibleAmountSum } from "@/components/ui/VisibleAmountSum";
import { XlsxExportButton } from "@/components/ui/XlsxExportButton";
import { useAuthStore } from "@/store/auth";
import { naturalSortKey } from "@/utils/naturalSort";
import { downloadProtocolsXlsx } from "../api";

interface Protocol {
  id: string;
  protocol_number: string;
  order_number: string;
  project_name: string;
  contractor_name: string;
  is_final: boolean;
  is_withdrawn: boolean;
  without_amounts?: boolean;
  accepted_by_contractor: boolean | null;
  // Accepted POCK of a natural-person contractor still awaiting a UZ/UoD
  // contract from kadry/księgowość — drives the red nav badge.
  requires_hr_mandate?: boolean;
  // amount_net is nulled server-side for viewers without rate visibility.
  items: { amount_net: string | null }[];
  created_at: string;
}

// Contractor's POCK request still awaiting GK approval — no protocol row exists
// yet, so it is listed here as "wystawiony do zatwierdzenia".
interface PendingPockRequest {
  id: string;
  order: string;
  order_number: string;
  project_name: string;
  contractor_name: string;
  is_withdrawn: boolean;
  items: { amount_net: string | null }[];
  created_at: string;
}

type Row =
  | ({ kind: "protocol" } & Protocol)
  | ({ kind: "request" } & PendingPockRequest);

const REQUEST_ROW_LABEL = "Prośba o POCK";

// Net value of the row for sorting — only the amounts the viewer may see.
function rowValue(r: Row): number {
  return r.items.reduce(
    (sum, i) => sum + (i.amount_net != null ? parseFloat(i.amount_net) : 0),
    0,
  );
}

function rowNumberLabel(r: Row): string {
  return r.kind === "protocol" ? r.protocol_number : REQUEST_ROW_LABEL;
}

// A pending request is reviewed (approved/rejected) on the order page.
function rowHref(r: Row): string {
  return r.kind === "protocol" ? `/protocols/${r.id}` : `/orders/${r.order}`;
}

type SortField =
  | "protocol_number"
  | "order_number"
  | "project_name"
  | "contractor_name"
  | "amount"
  | "type"
  | "acceptance"
  | "created_at";
type SortDir = "asc" | "desc";

type TypeFilter = "" | "partial" | "final" | "withdrawn";
type AcceptanceFilter = "" | "pending" | "pending_approval" | "accepted" | "rejected";

const SELECT_CLS =
  "text-xs border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]";

function SortableTh({
  field, label, sortField, sortDir, onSort,
}: {
  field: SortField;
  label: string;
  sortField: SortField | null;
  sortDir: SortDir;
  onSort: (f: SortField) => void;
}) {
  const active = sortField === field;
  return (
    <th
      onClick={() => onSort(field)}
      className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 whitespace-nowrap"
    >
      {label}{" "}
      <span className={active ? "text-[var(--color-primary)]" : "opacity-30"}>
        {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
      </span>
    </th>
  );
}

// Rank used when sorting the Typ column: request < partial < final < withdrawn
function typeRank(r: Row): number {
  if (r.kind === "request") return 0;
  if (r.is_withdrawn) return 3;
  return r.is_final ? 2 : 1;
}

// Rank used when sorting the Akceptacja column:
// awaiting approval < pending < accepted < rejected < withdrawn
function acceptanceRank(r: Row): number {
  if (r.kind === "request") return 0;
  if (r.is_withdrawn) return 4;
  if (r.accepted_by_contractor === null) return 1;
  return r.accepted_by_contractor ? 2 : 3;
}

// Default order: requests awaiting GK decision first, withdrawn last, API order otherwise.
function defaultRank(r: Row): number {
  if (r.kind === "request") return 0;
  return r.is_withdrawn ? 2 : 1;
}

export function ProtocolsListPage() {
  const user = useAuthStore((s) => s.user);
  // The /protocols nav badge (protocols_needing_hr_mandate) is only shown to
  // księgowość / kadry / CEO, so mirror it per-row for the same audience.
  const canSeeHrMandate =
    user?.role === "accountant" ||
    user?.role === "hr" ||
    (user?.role === "director" && !!user?.is_ceo);

  const { data, isLoading } = useQuery({
    queryKey: ["protocols"],
    queryFn: () =>
      apiClient.get<{ data: Protocol[] }>("/protocols/").then((r) => r.data.data),
  });

  // A withdrawn request keeps status=pending server-side — drop it here.
  const { data: pendingRequests, isLoading: isLoadingRequests } = useQuery({
    queryKey: ["protocols", "pending-pock-requests"],
    queryFn: () =>
      apiClient
        .get<{ data: PendingPockRequest[] }>("/protocols/pock-requests/", {
          params: { status: "pending" },
        })
        .then((r) => r.data.data.filter((pr) => !pr.is_withdrawn)),
  });

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [filterContractor, setFilterContractor] = useState("");
  const [filterProject, setFilterProject] = useState("");
  const [filterType, setFilterType] = useState<TypeFilter>("");
  const [filterAcceptance, setFilterAcceptance] = useState<AcceptanceFilter>("");

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  const rows = useMemo<Row[]>(
    () => [
      ...(pendingRequests ?? []).map((r) => ({ kind: "request" as const, ...r })),
      ...(data ?? []).map((p) => ({ kind: "protocol" as const, ...p })),
    ],
    [data, pendingRequests],
  );

  // Filter options come from the loaded rows (the endpoint returns names, not
  // ids), so the lists only offer values that actually occur in the table.
  const contractorOptions = useMemo(() => {
    const names = [...new Set(rows.map((r) => r.contractor_name).filter(Boolean))];
    return names.sort((a, b) => a.localeCompare(b, "pl")).map((n) => ({ value: n, label: n }));
  }, [rows]);

  const projectOptions = useMemo(() => {
    const names = [...new Set(rows.map((r) => r.project_name).filter(Boolean))];
    return names.sort((a, b) => a.localeCompare(b, "pl")).map((n) => ({ value: n, label: n }));
  }, [rows]);

  const visible = useMemo(() => {
    let list = rows;
    if (filterContractor) list = list.filter((r) => r.contractor_name === filterContractor);
    if (filterProject) list = list.filter((r) => r.project_name === filterProject);
    if (filterType) {
      // Częściowy/Końcowy/Wycofany describe an issued protocol — requests have no type yet.
      list = list.filter(
        (r) =>
          r.kind === "protocol" &&
          (filterType === "withdrawn"
            ? r.is_withdrawn
            : !r.is_withdrawn && (filterType === "final") === r.is_final),
      );
    }
    if (filterAcceptance) {
      list = list.filter((r) => {
        if (r.kind === "request") {
          // Awaiting-approval requests count as pending, plus their own dedicated filter.
          return filterAcceptance === "pending" || filterAcceptance === "pending_approval";
        }
        if (filterAcceptance === "pending_approval") return false;
        if (r.is_withdrawn) return false;
        if (filterAcceptance === "pending") return r.accepted_by_contractor === null;
        return r.accepted_by_contractor === (filterAcceptance === "accepted");
      });
    }

    if (!sortField) {
      return [...list].sort((a, b) => defaultRank(a) - defaultRank(b));
    }

    const dir = sortDir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "protocol_number":
          cmp = naturalSortKey(rowNumberLabel(a)).localeCompare(naturalSortKey(rowNumberLabel(b)), "pl");
          break;
        case "order_number":
          cmp = naturalSortKey(a.order_number).localeCompare(naturalSortKey(b.order_number), "pl");
          break;
        case "project_name":
          cmp = a.project_name.localeCompare(b.project_name, "pl");
          break;
        case "contractor_name":
          cmp = a.contractor_name.localeCompare(b.contractor_name, "pl");
          break;
        case "amount":
          cmp = rowValue(a) - rowValue(b);
          break;
        case "type":
          cmp = typeRank(a) - typeRank(b);
          break;
        case "acceptance":
          cmp = acceptanceRank(a) - acceptanceRank(b);
          break;
        case "created_at":
          cmp = a.created_at.localeCompare(b.created_at);
          break;
      }
      return cmp * dir;
    });
  }, [rows, filterContractor, filterProject, filterType, filterAcceptance, sortField, sortDir]);

  const hasActiveFilters = !!filterContractor || !!filterProject || !!filterType || !!filterAcceptance;

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Protokoły POCK</h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1" />
        </div>
        {/* Lista filtruje i sortuje w przeglądarce — do pliku idą dokładnie widoczne
            wiersze, w bieżącej kolejności. */}
        <XlsxExportButton
          label="Pobierz protokoły (.xlsx)"
          title="Pobierz odfiltrowane protokoły (w bieżącej kolejności) jako arkusz Excel"
          count={visible.length}
          disabled={isLoading || isLoadingRequests}
          download={() => downloadProtocolsXlsx(visible.map((r) => ({ kind: r.kind, id: r.id })))}
        />
      </div>
      {isLoading || isLoadingRequests ? (
        <PageLoader />
      ) : (
        <>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="w-64">
              <SearchableSelect
                placeholder="Wszyscy podwykonawcy"
                value={filterContractor}
                onChange={(e) => setFilterContractor(e.target.value)}
                options={contractorOptions}
              />
            </div>
            <div className="w-64">
              <SearchableSelect
                placeholder="Wszystkie przedsięwzięcia"
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
                options={projectOptions}
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as TypeFilter)}
              className={SELECT_CLS}
            >
              <option value="">Wszystkie typy</option>
              <option value="partial">Częściowy</option>
              <option value="final">Końcowy</option>
              <option value="withdrawn">Wycofany</option>
            </select>
            <select
              value={filterAcceptance}
              onChange={(e) => setFilterAcceptance(e.target.value as AcceptanceFilter)}
              className={SELECT_CLS}
            >
              <option value="">Wszystkie akceptacje</option>
              <option value="pending">Oczekuje</option>
              <option value="pending_approval">Oczekuje na zatwierdzenie</option>
              <option value="accepted">Zaakceptowany</option>
              <option value="rejected">Odrzucony</option>
            </select>
            {hasActiveFilters && (
              <button
                onClick={() => {
                  setFilterContractor("");
                  setFilterProject("");
                  setFilterType("");
                  setFilterAcceptance("");
                }}
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 underline"
              >
                Wyczyść filtry
              </button>
            )}
          </div>
          {visible.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 p-8 text-center text-gray-400 dark:text-gray-500 text-sm">
              {hasActiveFilters ? "Brak protokołów spełniających kryteria" : "Brak protokołów"}
            </div>
          ) : (
            <>
              {/* Mobile card list */}
              <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {visible.map((r) => (
                  <div key={r.id} className={`px-4 py-3 ${r.kind === "protocol" && r.is_withdrawn ? "opacity-60" : ""}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <Link
                          to={rowHref(r)}
                          className={`font-medium hover:underline block truncate ${r.kind === "protocol" && r.is_withdrawn ? "line-through text-gray-400 dark:text-gray-500" : "text-[var(--color-primary)]"}`}
                        >
                          {rowNumberLabel(r)}
                        </Link>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {r.order_number}{r.contractor_name ? ` · ${r.contractor_name}` : ""}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          Wartość: <VisibleAmountSum values={r.items.map((i) => i.amount_net)} />
                        </p>
                        {r.project_name && (
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                            {r.project_name}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {new Date(r.created_at).toLocaleDateString("pl")}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        {r.kind === "request" ? (
                          <Badge color="yellow">Oczekuje na zatwierdzenie</Badge>
                        ) : r.is_withdrawn ? (
                          <Badge color="gray">Wycofany</Badge>
                        ) : (
                          <>
                            <Badge color={r.is_final ? "purple" : "blue"}>
                              {r.is_final ? "Końcowy" : "Częściowy"}
                            </Badge>
                            {r.without_amounts && <Badge color="orange">Bez kwot</Badge>}
                            <Badge
                              color={r.accepted_by_contractor === null ? "gray" : r.accepted_by_contractor ? "green" : "red"}
                            >
                              {r.accepted_by_contractor === null ? "Oczekuje" : r.accepted_by_contractor ? "Zaakceptowany" : "Odrzucony"}
                            </Badge>
                            {canSeeHrMandate && r.requires_hr_mandate && (
                              <Badge color="red">Wymaga UZ/UoD</Badge>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop table */}
              <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
                <table className="w-full text-xs min-w-[840px]">
                  <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <SortableTh field="protocol_number" label="Nr protokołu" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh field="order_number" label="Zamówienie" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh field="contractor_name" label="Podwykonawca" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh field="project_name" label="Przedsięwzięcie" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh field="amount" label="Wartość" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh field="type" label="Typ" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      <SortableTh field="acceptance" label="Akceptacja" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                      {canSeeHrMandate && (
                        <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Rozliczenie</th>
                      )}
                      <SortableTh field="created_at" label="Data" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {visible.map((r) => (
                      <tr key={r.id} className={r.kind === "protocol" && r.is_withdrawn ? "bg-gray-50 dark:bg-gray-800/50 opacity-60" : "hover:bg-gray-50 dark:hover:bg-gray-700/50"}>
                        <td className="px-4 py-3 font-medium">
                          <Link
                            to={rowHref(r)}
                            className={`hover:underline ${r.kind === "protocol" && r.is_withdrawn ? "line-through text-gray-400 dark:text-gray-500" : "text-[var(--color-primary)]"}`}
                          >
                            {rowNumberLabel(r)}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{r.order_number}</td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{r.contractor_name}</td>
                        <td className="px-4 py-3 text-gray-400 dark:text-gray-500">{r.project_name}</td>
                        <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                          <VisibleAmountSum values={r.items.map((i) => i.amount_net)} />
                        </td>
                        <td className="px-4 py-3">
                          {r.kind === "request" ? (
                            <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                          ) : r.is_withdrawn ? (
                            <Badge color="gray">Wycofany</Badge>
                          ) : (
                            <>
                              <Badge color={r.is_final ? "purple" : "blue"}>
                                {r.is_final ? "Końcowy" : "Częściowy"}
                              </Badge>
                              {r.without_amounts && <Badge color="orange">Bez kwot</Badge>}
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {r.kind === "request" ? (
                            <Badge color="yellow">Oczekuje na zatwierdzenie</Badge>
                          ) : r.is_withdrawn ? (
                            <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                          ) : (
                            <Badge
                              color={
                                r.accepted_by_contractor === null
                                  ? "gray"
                                  : r.accepted_by_contractor
                                  ? "green"
                                  : "red"
                              }
                            >
                              {r.accepted_by_contractor === null
                                ? "Oczekuje"
                                : r.accepted_by_contractor
                                ? "Zaakceptowany"
                                : "Odrzucony"}
                            </Badge>
                          )}
                        </td>
                        {canSeeHrMandate && (
                          <td className="px-4 py-3">
                            {r.kind === "protocol" && !r.is_withdrawn && r.requires_hr_mandate ? (
                              <Badge color="red">Wymaga UZ/UoD</Badge>
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                          {new Date(r.created_at).toLocaleDateString("pl")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
